import { t } from 'react-switch-lang';
import { isDevelopment } from './HostingEnv';
import { toErrorPage } from '../components/ErrorDisplay';
import { FIELD_VALIDATION_REGEX } from './Validations';

/**
 * @typedef {Object} SmartPaymentLinkQueryParams
 * Optional properties won't exist in the object if not provided.
 * @property {string} [email] required if using invoiceno
 * @property {string} [name] required if using invoiceno
 * @property {string} [clientref] required if using invoiceno
 * @property {string} [amount] required if using invoiceno
 * @property {string} [invoiceno] special handling if provided
 * @property {string} [field1]
 * @property {string} [field2]
 * @property {string} [field3]
 * @property {string} [field4]
 * @property {string} [field5]
 * @property {string} [platform] Stored as LheCodes.ReferralProgram in DB
 * @property {'Intuit'} [siteMode] Stored as LheCodes.SiteMode in DB
 * @property {string} [referrer] the Referer header of the original request, if available
 */

/** @return {import('next').Redirect} */
export function redirectToError(lang, titleKey) {
  const title = titleKey ? t(titleKey, null, lang) : '';
  return {
    destination: `/${lang}${toErrorPage(title)}`,
    permanent: false,
  };
}

/**
 * @param {import('querystring').ParsedUrlQuery} query
 * @param {string[]} params
 * @returns {Object<string, string>}
 */
export function filterQueryParams(query, params) {
  const lowerCasedQuery = Object.fromEntries(
    Object.entries(query).map(([k, v]) => [k.toLowerCase(), v])
  );
  return params.reduce((acc, paramName) => {
    const val = lowerCasedQuery[paramName.toLowerCase()];
    if (!val) return acc;
    acc[paramName] = typeof val === 'string' ? val : val[0];
    return acc;
  }, {});
}

/**
 * @param {SmartPaymentLinkQueryParams|null} params
 * @param {import('../context/PaymentProcessContext').Field[]} Fields
 */
export function validateRequiredParamsForInvoice(params, Fields) {
  if (!params) return false;

  // required fields
  if (!params.invoiceno || !params.email || !params.name || !params.clientref || !params.amount) {
    return false;
  }

  // any configured PARTNERFIELD must be provided in the query
  return Fields.every(({ DBField }) => !DBField.startsWith('PARTNERFIELD') || params[`field${DBField.at(-1)}`]);
}

/**
 * @param {import('../context/PaymentProcessContext').AccountDetails|null} accountDetails
 * @param {import('../context/PaymentProcessContext').Field[]} Fields
 */
export function validateAccountDetailsForInvoice(accountDetails, Fields) {
  if (!accountDetails) return false;

  // map accountDetails to SmartPaymentLinkQueryParams
  /** @type {SmartPaymentLinkQueryParams} */
  const params = {
    invoiceno: accountDetails.InvoiceNo,
    email: accountDetails.Email,
    name: accountDetails.FullName,
    clientref: accountDetails.ClientRef,
    amount: accountDetails.PaymentAmount,
    field1: accountDetails.PARTNERFIELD01,
    field2: accountDetails.PARTNERFIELD02,
    field3: accountDetails.PARTNERFIELD03,
    field4: accountDetails.PARTNERFIELD04,
    field5: accountDetails.PARTNERFIELD05,
  };

  return validateRequiredParamsForInvoice(params, Fields);
}

/**
 * Validates and filters the provided query params. Returns null if query params are empty.
 * If the query params are invalid, throws a Redirect object to be returned in getServerSideProps.
 * @param {import('querystring').ParsedUrlQuery} query
 * @param {import('../context/PaymentProcessContext').PartnerInfo} partnerInfo
 * @param {import('aws4fetch').AwsClient} aws
 * @param {string} lang
 * @return {Promise<SmartPaymentLinkQueryParams|null>}
 * @throws {import('next').Redirect} if the query params are invalid
 */
export async function validateSmartPaymentLink(query, partnerInfo, aws, lang) {
  /** @type {SmartPaymentLinkQueryParams} */
  const params = filterQueryParams(query, [
    'email', 'name', 'clientref', 'amount', 'invoiceno', 'field1', 'field2', 'field3', 'field4', 'field5', 'platform', 'siteMode',
  ]);

  // ignore invalid amount in query params
  if (params.amount && !FIELD_VALIDATION_REGEX.VALID_DECIMAL.test(params.amount)) {
    delete params.amount;
  }

  // validate on the frontend if empty query params, to allow users to refresh while on the page
  if (Object.keys(params).length === 0) return null;

  const { PSWebConfig: { Fields }, PartnerSeq } = partnerInfo;

  const partnerSetForInvoice = Fields.some((f) => f.DBField === 'InvoiceNo');

  // check if this partner's clientref contains sensitive data (DBMask is set)
  const clientRefField = Fields.find((f) => f.DBField === 'ClientRef');
  if (clientRefField?.DBMask) {
    delete params.clientref;
  }

  if (partnerSetForInvoice) {
    if (!validateRequiredParamsForInvoice(params, Fields)) throw redirectToError(lang, 'SmartLinkRequiredOrInvalid');

    // check if invoice is already paid
    const apiResp = await aws.fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/v1/CheckPartnerInvoiceNo`,
      {
        method: 'POST',
        headers: { 'x-api-key': process.env.NEXT_PUBLIC_API_KEY },
        body: JSON.stringify({
          PartnerSeq,
          InvoiceNo: params.invoiceno,
        }),
      }
    ).then((r) => r.json()).catch((e) => {
      console.error(e); // eslint-disable-line no-console
      throw redirectToError(lang);
    });

    if (apiResp.Result !== 0) {
      // eslint-disable-next-line no-console
      console.log({
        Message: `CheckPartnerInvoiceNo failed for: PartnerSeq: ${PartnerSeq}, InvoiceNo: ${params.invoiceno}`,
        apiResp,
      });
      throw redirectToError(lang);
    }

    if (apiResp.Paid) throw redirectToError(lang, 'InvoiceNo_SubsequentPaymentBlocking_ErrorMessage');
  } else {
    delete params.invoiceno;
  }

  // ignore optional fields that are not configured in PSWebConfig
  for (let i = 1; i <= 5; i += 1) {
    const notConfigured = Fields.every((f) => f.DBField.toUpperCase() !== `PARTNERFIELD0${i}`);
    if (notConfigured) delete params[`field${i}`];
  }

  if (isDevelopment) {
    if (params.amount === 'random') {
      params.amount = (Math.random() * 1990 + 10).toFixed(2);
    }
  }

  return params;
}
